/**
 * Performs a deep comparison between an old value and a new value.
 *
 * Supports primitive values, arrays, and plain objects, including nested
 * arrays and objects. Object property order does not affect equality,
 * while array item order does.
 *
 * @param {*} oldValue - The previous value to compare.
 * @param {*} newValue - The new value to compare.
 * @returns {boolean} `true` if both values are deeply equal; otherwise `false`.
 */

export const isValuesEqual = (oldValue, newValue) => {
  if (Object.is(oldValue, newValue)) {
    return true;
  }

  if (
    typeof oldValue !== "object" ||
    oldValue === null ||
    typeof newValue !== "object" ||
    newValue === null
  ) {
    return false;
  }

  if (Array.isArray(oldValue) || Array.isArray(newValue)) {
    if (!Array.isArray(oldValue) || !Array.isArray(newValue)) {
      return false;
    }

    if (oldValue.length !== newValue.length) {
      return false;
    }

    return oldValue.every((item, index) =>
      isValuesEqual(item, newValue[index]),
    );
  }

  const oldKeys = Object.keys(oldValue);
  const newKeys = Object.keys(newValue);

  if (oldKeys.length !== newKeys.length) {
    return false;
  }

  return oldKeys.every(
    (key) =>
      Object.hasOwn(newValue, key) &&
      isValuesEqual(oldValue[key], newValue[key]),
  );
};

/**
 * Formats a price using the specified country's currency.
 *
 * @param {number|string} price - The price to format.
 * @param {"AU"|"NZ"} country - The country code.
 * @returns {string} The formatted price, or an empty string if invalid.
 */
export const formatPrice = (price, country = "AU") => {
  const numericPrice = Number(String(price).replace(/[$,]/g, ""));

  if (Number.isNaN(numericPrice)) {
    return "";
  }

  const currencyConfig = {
    AU: {
      locale: "en-AU",
      currency: "AUD",
    },
    NZ: {
      locale: "en-NZ",
      currency: "NZD",
    },
  };

  const config = currencyConfig[country];

  if (!config) {
    return "";
  }

  return new Intl.NumberFormat(config.locale, {
    style: "currency",
    currency: config.currency,
    maximumFractionDigits: 0,
  }).format(numericPrice);
};
